import { useState } from 'react'

import { PayrollPolicyEmployeesDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import { useConcepts } from 'modules/payroll_bundle/hooks/useConcepts'
import { usePayrollSummaryEmployeeMatches } from 'modules/payroll_bundle/hooks/usePayrollSummaryEmployeeMatches'
import { ImportData, MatchReason } from 'modules/payroll_bundle/lib/PayrollResultContext'
import { DEFAULT_RESULT_CONCEPT } from 'modules/payroll_bundle/lib/constants'

import { LabelToConceptId } from '../../../types'

interface Props {
  payrollImportId: number
  payrollPolicyPeriodId: number
  labelToConceptId: LabelToConceptId
}

// Builds importedEmployees for PayrollResultContext (from these, ResultsPage
// will build `resultItems` by just adding an id).
//
// [
//   {
//       "last_name": "The Boss",
//       "net_salary": 3000,
//       "first_name": "Bob",
//       "userId": 21,
//       "IBAN": null,
//       "employee_id": 5,
//       "full_name": "Bob The Boss"
//       "concepts": [{ id, label, value... }, ...]
//   },
//   ...
// ]
//
// Uses GraphQL's getPayrollSummaryImport to get the parsed content of the file
// specified by payrollImportId. The backend will have figured out if the
// data in the file was laid out horizontally or vertically, and will have
// matched each item to the corresponding employee.
//
// NOTE
//
// Replaces matchData(...) and useMatchEmployeeData(...)
//
// matchData(...) was used to find out if data was laid out horizontally or
// vertically. The returned value was used to set PayrollResultContext's
// matchedData.
//
// useMatchEmployeeData(...) used matchedData and the match_employees_data
// endpoint to match the imported rows to employees.
//
const useBuildImportedEmployees = ({
  payrollImportId,
  payrollPolicyPeriodId,
  labelToConceptId,
}: Props) => {
  const [importedEmployees, setImportedEmployees] = useState<ImportData[]>([])
  const { loading: fetchingPolicyEmployees, data: payrollPolicyEmployeeData } = useQuery(
    PayrollPolicyEmployeesDocument,
    {
      variables: { payrollPolicyPeriodId: payrollPolicyPeriodId },
    }
  )

  const selectedPeriodEmployees = payrollPolicyEmployeeData?.contracts.payrollPolicyEmployees || []

  // matches from the new importer
  const { fetching: fetchingMatches, data: matchesData } = usePayrollSummaryEmployeeMatches(
    Number(payrollImportId)
  )

  const { concepts } = useConcepts()
  const labelToConceptName = Object.keys(labelToConceptId).reduce(
    (acc: Record<string, string>, label) => {
      const importedConceptId = labelToConceptId[label]
      const concept = concepts.find((_concept) => _concept.id === importedConceptId)

      if (concept) {
        acc[label] = concept.name
      }
      return acc
    },
    {}
  )

  const selectedPeriodEmployeeIds = new Set(
    selectedPeriodEmployees.map((periodEmployee) => periodEmployee?.employee?.id)
  )

  interface ConceptsMainInformation {
    netConceptAmountInCents?: number
    grossConceptAmountInCents?: number
    companyCostConceptAmountInCents?: number
  }

  const calculateConceptsFromLines = (
    conceptLines: {
      value: string
      label: string
    }[]
  ) => {
    return conceptLines.reduce((acc: ConceptsMainInformation, concept) => {
      // TODO-IMPORTER: why do we need gross and company cost?
      switch (labelToConceptName[concept.label]) {
        case DEFAULT_RESULT_CONCEPT.net_pay:
          return { ...acc, netConceptAmountInCents: Number(concept.value) }
        case DEFAULT_RESULT_CONCEPT.gross_pay:
          return { ...acc, grossConceptAmountInCents: Number(concept.value) }
        case DEFAULT_RESULT_CONCEPT.company_cost:
          return { ...acc, companyCostConceptAmountInCents: Number(concept.value) }
        default:
          return acc
      }
    }, {})
  }

  const getMatchedEmployees = (): ImportData[] => {
    return matchesData.map((match) => {
      const {
        netConceptAmountInCents,
        grossConceptAmountInCents,
        companyCostConceptAmountInCents,
      } = calculateConceptsFromLines(match.conceptLines)
      // TODO-IMPORTER: in new importer check employee directly
      const employeeId = match?.employeeId ? match.employeeId : null

      const isEmployeeInPolicyPeriod =
        employeeId !== null ? selectedPeriodEmployeeIds.has(employeeId) : false

      // eslint-disable-next-line no-type-assertion/no-type-assertion
      const matchReason = MatchReason[match.matchReason as keyof typeof MatchReason]

      return {
        id: match.row,
        employee_id: isEmployeeInPolicyPeriod ? employeeId : null,
        summaryImportEmployeeId: match.row,
        userId: null,
        first_name: match.firstName ?? '',
        last_name: match.lastName ?? '',
        full_name: match.fullName ? match.fullName : `${match.firstName} ${match.lastName}`,
        net_salary: netConceptAmountInCents ?? null,
        gross_salary: grossConceptAmountInCents ?? null,
        company_costs: companyCostConceptAmountInCents ?? null,
        match_reason: matchReason ?? null,
        company_identifier: match.companyIdentifier ?? null,
        concepts: match.conceptLines.map((conceptLine) => {
          return {
            concept_id: Number(conceptLine.concept_id),
            label: String(conceptLine.label),
            value: String(conceptLine.value),
          }
        }),
      }
    })
  }

  const importedEmployeesFromResults = getMatchedEmployees()

  // First time that this runs since loading finished?
  if (!fetchingPolicyEmployees && !fetchingMatches && importedEmployees.length === 0) {
    setImportedEmployees(importedEmployeesFromResults)
  }

  return { importedEmployees, loading: fetchingMatches || fetchingPolicyEmployees }
}

export default useBuildImportedEmployees
